/* eslint-disable @typescript-eslint/no-unused-vars */
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { SettingsService } from '../../../core/services/settings.service';
import {
  CorregirClientePayload,
  SolicitudCancelacionResponse,
  FiltroClientesCancelacionesDetalles,
  ReenvioClientesRechazadosResponse,
  EstadosSolicitudCancelacionResponse,
  EnviarSolicitudCancelacionaResponse,
  ClienteCancelacionDetallesListResponse,
  OperacionSolicitud,
  PayloadRechazo,
  PayloadAprobado,
  PayloadConfirmacion,
  PermisoCancelacionFraude,
  EstadosSolicitudCancelacion,
} from './cancelaciones-fraude.model';
import { BehaviorSubject, Observable, of, shareReplay, switchMap, take, timer } from 'rxjs';
import { MenuItem, MenuItemConPermisos } from '../../../layouts/sidebar/menu.model';

@Injectable({
  providedIn: 'root',
})
export class CancelacionesFraudesService {
  private http = inject(HttpClient);
  private setting = inject(SettingsService);
  private baseUrl = this.setting.URL_BASE;

  #permisos = new BehaviorSubject<PermisoCancelacionFraude[]>([]);
  permisos$ = this.#permisos.asObservable();

  #estadosSolicitudCancelacionCache?: Observable<EstadosSolicitudCancelacion[]>;

  constructor() {}

  setPermisos(permisos: PermisoCancelacionFraude[]) {
    this.#permisos.next(permisos);
  }

  clearPermisos() {
    this.#permisos.next([]);
  }

  verificarMenuParaCancelacionFraudes(menu: MenuItem[]) {
    const menuAdministracion = this.getMenuAdmin(menu);
    if (!menuAdministracion) return;

    const rolCancelacionFruade = this.getRolCancelacionFraude(menuAdministracion!);
    if (rolCancelacionFruade) {
      this.configurarPermisosParaCancelacionFraude(rolCancelacionFruade);
    }
  }

  configurarPermisosParaCancelacionFraude(rol: MenuItemConPermisos) {
    const permisosCancelacion: PermisoCancelacionFraude[] = rol.permisos;
    this.setPermisos(permisosCancelacion);
  }

  getRolCancelacionFraude(menuAdministracion: MenuItem): MenuItemConPermisos {
    return menuAdministracion?.menu.find((menu: MenuItem) => menu.url?.includes('cancelaciones-fraude'));
  }

  getMenuAdmin(menu: MenuItem[]) {
    return menu.find(menu => menu.nombre?.toLowerCase()?.includes('admin'));
  }

  obtenerEstadosSolicitudCancelacion(): Observable<EstadosSolicitudCancelacion[]> {
    if (!this.#estadosSolicitudCancelacionCache)
      this.#estadosSolicitudCancelacionCache = this.http
        .get<EstadosSolicitudCancelacion[]>(`${this.baseUrl}solicitudes-cancelacion/list-estados`)
        .pipe(shareReplay(1));

    return this.#estadosSolicitudCancelacionCache;
  }

  obtenerSolicitudesCancelacion(filter: any): Observable<SolicitudCancelacionResponse> {
    return this.http.post<SolicitudCancelacionResponse>(`${this.baseUrl}solicitudes-cancelacion/list`, filter);
  }

  enviarSolicitud(file: File): Observable<EnviarSolicitudCancelacionaResponse> {
    const formData = new FormData();
    formData.append('archivo', file);
    return this.http.post<EnviarSolicitudCancelacionaResponse>(
      `${this.baseUrl}solicitudes-cancelacion/enviar`,
      formData,
    );
  }

  corregirCliente(filter: CorregirClientePayload) {
    return this.http.put(`${this.baseUrl}solicitudes-cancelacion/corregir`, filter);
  }

  obtenerListadoPorSolicitud(
    filter: FiltroClientesCancelacionesDetalles,
  ): Observable<ClienteCancelacionDetallesListResponse> {
    return this.http.post<ClienteCancelacionDetallesListResponse>(
      `${this.baseUrl}solicitudes-cancelacion/list-registros`,
      filter,
    );
  }

  confirmarSolicitud(
    archivoId: number,
    operacion: OperacionSolicitud,
    motivo?: string,
  ): Observable<ReenvioClientesRechazadosResponse> {
    const payload: PayloadConfirmacion =
      operacion === 'aprobar'
        ? ({
            archivoId,
            operacion,
          } as PayloadAprobado)
        : ({
            archivoId,
            operacion,
            motivo,
          } as PayloadRechazo);

    return this.http.put<ReenvioClientesRechazadosResponse>(`${this.baseUrl}solicitudes-cancelacion/enviar`, payload);
  }

  aprobarSolicitud(solicitudId: number): Observable<ReenvioClientesRechazadosResponse> {
    return this.http.put<ReenvioClientesRechazadosResponse>(`${this.baseUrl}solicitudes-cancelacion/aprobar`, {
      solicitudId,
    });
  }

  rechazarSolicitud(solicitudId: number, motivo: string): Observable<ReenvioClientesRechazadosResponse> {
    return this.http.put<ReenvioClientesRechazadosResponse>(`${this.baseUrl}solicitudes-cancelacion/rechazar`, {
      solicitudId,
      motivo,
    });
  }
}
